import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

import { ImageCrop, MIN_VISIBLE_FRACTION, normalizeImageCrop } from './extensions/image-crop.util';

export interface ImageCropResult {
  /** null = not cropped (original size restored). */
  crop: ImageCrop | null;
  naturalWidth: number | null;
  naturalHeight: number | null;
}

type CropHandle = 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'sw' | 'se' | 'move';

const NO_CROP: ImageCrop = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * Crop dialog for a note image (Edit → image menu → Crop). Shows the whole original image with the
 * part that stays visible framed; the frame's edges and corners are dragged (mouse or finger) to crop
 * horizontally and/or vertically, and the frame itself can be dragged to show another part.
 * Only the crop values are returned — the image file is never changed.
 */
@Component({
  selector: 'app-image-crop-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
  template: `
    <p-dialog
      header="Crop image"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onDialogHide()"
      [style]="{ width: '760px', maxWidth: '96vw' }"
      appendTo="body">
      <p class="crop-hint">Drag the edges or corners of the frame to crop; drag inside the frame to move it. The image file itself is not changed.</p>

      <div class="crop-stage-outer">
        <div class="crop-stage" #stage data-testid="crop-stage">
          <img class="crop-stage__img" [src]="src" alt="" draggable="false" (load)="onImageLoad($event)" />
          @if (imageLoaded) {
            <div
              class="crop-box"
              data-testid="crop-box"
              [style.left.%]="crop.left * 100"
              [style.top.%]="crop.top * 100"
              [style.right.%]="crop.right * 100"
              [style.bottom.%]="crop.bottom * 100"
              (pointerdown)="startDrag($event, 'move')">
              @for (handle of handles; track handle.id) {
                <button
                  type="button"
                  class="crop-handle crop-handle--{{ handle.id }}"
                  [attr.data-handle]="handle.id"
                  [attr.aria-label]="handle.label"
                  (pointerdown)="startDrag($event, handle.id)"
                  (keydown)="onHandleKey($event, handle.id)">
                </button>
              }
            </div>
          }
        </div>
      </div>

      <div class="crop-readout" data-testid="crop-readout">
        Cropped: top {{ pct(crop.top) }}%, right {{ pct(crop.right) }}%, bottom {{ pct(crop.bottom) }}%, left {{ pct(crop.left) }}%
      </div>

      <ng-template pTemplate="footer">
        <div class="crop-footer">
          <p-button
            label="Restore original"
            icon="pi pi-refresh"
            severity="secondary"
            [text]="true"
            [disabled]="!isCropped"
            (onClick)="resetCrop()">
          </p-button>
          <span class="crop-footer__actions">
            <p-button label="Cancel" severity="secondary" [outlined]="true" (onClick)="visible = false; visibleChange.emit(false)"></p-button>
            <p-button label="Apply" icon="pi pi-check" [disabled]="!imageLoaded" (onClick)="apply()"></p-button>
          </span>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .crop-hint {
        margin: 0 0 0.75rem;
        font-size: 0.875rem;
        color: var(--p-text-muted-color);
      }
      /* Clips the dimming around the frame to this area; the padding leaves room for the handles. */
      .crop-stage-outer {
        display: flex;
        justify-content: center;
        overflow: hidden;
        padding: 20px;
        border-radius: 0.5rem;
        background: repeating-conic-gradient(#e5e7eb 0% 25%, #f8fafc 0% 50%) 50% / 20px 20px;
      }
      :host-context(.p-dark) .crop-stage-outer {
        background: repeating-conic-gradient(#1f2937 0% 25%, #111827 0% 50%) 50% / 20px 20px;
      }
      .crop-stage {
        position: relative;
        display: inline-block;
        line-height: 0;
        touch-action: none;
        user-select: none;
        -webkit-user-select: none;
        -webkit-touch-callout: none;
      }
      .crop-stage__img {
        display: block;
        max-width: 100%;
        max-height: min(60vh, 560px);
        width: auto;
        height: auto;
        pointer-events: none;
        -webkit-user-drag: none;
      }
      .crop-box {
        position: absolute;
        cursor: move;
        outline: 2px solid #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.6), 0 0 0 100vmax rgba(15, 23, 42, 0.6);
      }
      /* Each handle is a finger-sized (36px) target with a small visible bar or corner inside. */
      .crop-handle {
        position: absolute;
        width: 36px;
        height: 36px;
        padding: 0;
        border: 0;
        background: transparent;
        touch-action: none;
        z-index: 1;
      }
      .crop-handle::after {
        content: '';
        position: absolute;
        background: #111827;
        border: 2px solid #fff;
        border-radius: 2px;
      }
      .crop-handle:focus-visible::after {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }
      .crop-handle--n, .crop-handle--s { left: calc(50% - 18px); cursor: ns-resize; }
      .crop-handle--e, .crop-handle--w { top: calc(50% - 18px); cursor: ew-resize; }
      .crop-handle--n { top: -18px; }
      .crop-handle--s { bottom: -18px; }
      .crop-handle--e { right: -18px; }
      .crop-handle--w { left: -18px; }
      .crop-handle--n::after, .crop-handle--s::after { left: 6px; top: 14px; width: 24px; height: 8px; }
      .crop-handle--e::after, .crop-handle--w::after { left: 14px; top: 6px; width: 8px; height: 24px; }
      .crop-handle--nw { left: -18px; top: -18px; cursor: nwse-resize; }
      .crop-handle--se { right: -18px; bottom: -18px; cursor: nwse-resize; }
      .crop-handle--ne { right: -18px; top: -18px; cursor: nesw-resize; }
      .crop-handle--sw { left: -18px; bottom: -18px; cursor: nesw-resize; }
      .crop-handle--nw::after, .crop-handle--ne::after, .crop-handle--sw::after, .crop-handle--se::after {
        left: 12px;
        top: 12px;
        width: 12px;
        height: 12px;
      }
      .crop-readout {
        margin-top: 0.75rem;
        font-size: 0.8125rem;
        color: var(--p-text-muted-color);
        text-align: center;
        font-variant-numeric: tabular-nums;
      }
      .crop-footer {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
        flex-wrap: wrap;
      }
      .crop-footer__actions {
        display: flex;
        gap: 0.5rem;
        margin-left: auto;
      }
    `,
  ],
})
export class ImageCropDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  /** The image as shown in the editor (blob or remote URL). */
  @Input() src: string | null = null;
  @Input() initialCrop: ImageCrop | null = null;
  @Output() cropApplied = new EventEmitter<ImageCropResult>();

  @ViewChild('stage') stageRef?: ElementRef<HTMLElement>;

  readonly handles: Array<{ id: CropHandle; label: string }> = [
    { id: 'nw', label: 'Crop top and left' },
    { id: 'n', label: 'Crop top' },
    { id: 'ne', label: 'Crop top and right' },
    { id: 'e', label: 'Crop right' },
    { id: 'se', label: 'Crop bottom and right' },
    { id: 's', label: 'Crop bottom' },
    { id: 'sw', label: 'Crop bottom and left' },
    { id: 'w', label: 'Crop left' },
  ];

  crop: ImageCrop = { ...NO_CROP };
  imageLoaded = false;
  private naturalWidth: number | null = null;
  private naturalHeight: number | null = null;
  private stopDrag: (() => void) | null = null;

  get isCropped(): boolean {
    return normalizeImageCrop(this.crop) !== null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] && this.visible) || changes['initialCrop'] || changes['src']) {
      this.crop = { ...(normalizeImageCrop(this.initialCrop) ?? NO_CROP) };
    }
    if (changes['src']) {
      this.imageLoaded = false;
    }
  }

  ngOnDestroy(): void {
    this.stopDrag?.();
  }

  pct(value: number): string {
    return String(Math.round(value * 1000) / 10);
  }

  onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.naturalWidth = img.naturalWidth || null;
    this.naturalHeight = img.naturalHeight || null;
    this.imageLoaded = true;
  }

  resetCrop(): void {
    this.crop = { ...NO_CROP };
  }

  apply(): void {
    this.cropApplied.emit({ crop: normalizeImageCrop(this.crop), naturalWidth: this.naturalWidth, naturalHeight: this.naturalHeight });
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onDialogHide(): void {
    this.stopDrag?.();
    this.visible = false;
    this.visibleChange.emit(false);
  }

  startDrag(event: PointerEvent, handle: CropHandle): void {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    const stage = this.stageRef?.nativeElement;
    if (!stage) return;
    event.preventDefault();
    event.stopPropagation();
    this.stopDrag?.();

    const rect = stage.getBoundingClientRect();
    const start = { ...this.crop };
    const startX = event.clientX;
    const startY = event.clientY;
    const pointerId = event.pointerId;
    const target = event.currentTarget as HTMLElement;
    try {
      target.setPointerCapture?.(pointerId);
    } catch {
      // The pointer is already gone — the window listeners below still follow it.
    }

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      const dx = rect.width ? (e.clientX - startX) / rect.width : 0;
      const dy = rect.height ? (e.clientY - startY) / rect.height : 0;
      this.crop = this.dragged(start, handle, dx, dy);
    };
    const onEnd = (e: PointerEvent) => {
      if (e.pointerId === pointerId) this.stopDrag?.();
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    this.stopDrag = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      if (target.hasPointerCapture?.(pointerId)) target.releasePointerCapture(pointerId);
      this.stopDrag = null;
    };
  }

  onHandleKey(event: KeyboardEvent, handle: CropHandle): void {
    const step = event.shiftKey ? 0.1 : 0.01;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    this.crop = this.dragged(this.crop, handle, move[0], move[1]);
  }

  /** The crop after moving `handle` by (dx, dy) fractions of the image, kept within the image and
   * never smaller than MIN_VISIBLE_FRACTION. */
  private dragged(start: ImageCrop, handle: CropHandle, dx: number, dy: number): ImageCrop {
    const min = MIN_VISIBLE_FRACTION;
    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    const next = { ...start };
    if (handle === 'move') {
      const mx = clamp(dx, -start.left, start.right);
      const my = clamp(dy, -start.top, start.bottom);
      next.left = start.left + mx;
      next.right = start.right - mx;
      next.top = start.top + my;
      next.bottom = start.bottom - my;
      return next;
    }
    if (handle.includes('w')) next.left = clamp(start.left + dx, 0, 1 - start.right - min);
    if (handle.includes('e')) next.right = clamp(start.right - dx, 0, 1 - start.left - min);
    if (handle.includes('n')) next.top = clamp(start.top + dy, 0, 1 - start.bottom - min);
    if (handle.includes('s')) next.bottom = clamp(start.bottom - dy, 0, 1 - start.top - min);
    return next;
  }
}
